import {ApolloClient, useApolloClient} from "@apollo/client";
import {Dispatch} from "redux";
import {useEffect, useState} from "react";
import {useStructureConfig} from "@/context/StructureConfigProvider";
import {areObjectsEqual} from "../../../tools";
import {getCachedData, wrapSaveToCache} from "../../../utils/formCache";
import {
    SaveLocationMutationHookResult,
    SaveLocationMutationVariables,
    useSaveLocationMutation,
} from "../queries.generated";

export type FieldError = {
    type: string;
    context: {[s: string]: any};
};

export type LocationFormData = SaveLocationMutationVariables;

type FormErrors = {[s in keyof Partial<LocationFormData>]: FieldError};

export type SubmitError = {
    type: string;
    location: string[];
    context: string;
};

export const defaultLocationData: LocationFormData = {
    id: 0,
    domainId: 0,
    name: "",
    callLimit: -1,
    bandwidthLimit: -1,
    description: "",
    ipAddress: null,
    pilotNumber: "",
    emergencyPrefix: null,
};

export class Controller {
    readonly data: LocationFormData;

    readonly client: ApolloClient<object>;
    readonly setData: (data: LocationFormData) => void;
    readonly dispatch: Dispatch<any>;
    readonly extraData: Partial<LocationFormData>;
    readonly errors: FormErrors;
    readonly setErrors: (e: FormErrors) => void;
    readonly initialData: LocationFormData;
    readonly emergencyPrefixIsEnabled: boolean;

    private mutation: SaveLocationMutationHookResult[0];

    constructor(initialData: LocationFormData, extraData: Partial<LocationFormData>, cacheKeyPostfix?: string) {
        this.client = useApolloClient();
        [this.data, this.setData] = useState<LocationFormData>(defaultLocationData);
        [this.errors, this.setErrors] = useState<FormErrors>({});
        this.extraData = extraData;
        this.initialData = initialData;
        const {isEnabledFeature} = useStructureConfig();
        this.emergencyPrefixIsEnabled = isEnabledFeature("emergencyPrefix");
        [this.mutation] = useSaveLocationMutation();

        useEffect(() => {
            this.setData(getCachedData(cacheKeyPostfix) || initialData);
        }, [cacheKeyPostfix, initialData]);

        wrapSaveToCache(this, this.saveNewData.name, cacheKeyPostfix);
    }

    updateData(name: keyof LocationFormData, value: any) {
        this.saveNewData({...this.data, [name]: value});
    }

    protected saveNewData(data: LocationFormData): LocationFormData {
        this.setData(data);
        return data;
    }

    get isChanged(): boolean {
        return !areObjectsEqual(this.initialData, this.data);
    }

    hasError(name: keyof LocationFormData): boolean {
        return !!this.errors[name];
    }

    reset() {
        this.saveNewData(this.initialData);
        this.setErrors({});
    }

    async submit(): Promise<{id: number}> {
        const data = {...this.data, ...this.extraData};
        if (!this.emergencyPrefixIsEnabled) delete data.emergencyPrefix;

        const {data: result} = await this.mutation({
            variables: {
                ...data,
            },
        });

        this.processErrors((result.saveLocation.errors as SubmitError[]) || []);

        return {
            id: result.saveLocation.id,
        };
    }

    private processErrors(errors: SubmitError[]) {
        const formErrors: FormErrors = {};
        for (const err of errors) {
            const key = err.location[0] as keyof LocationFormData;
            formErrors[key] = {
                type: err.type,
                context: err.context ? JSON.parse(err.context) : {},
            };
        }
        console.debug("set form errors", formErrors);
        this.setErrors(formErrors);
    }
}
